import { App, Button, Tag, Tooltip, type TableColumnsType } from 'antd';
import { Copy, Pencil, Trash2, Zap } from 'lucide-react';
import DsIcon from '@/components/Layout/DsIcon';
import { UserName } from '@/components/UserName';
import ProTable from '@/components/pro/ProTable';
import { formatTimestamp, copyToClipboard } from '@/utils/utils';
import { ApiError } from '@/api/errors';
import { type MaasCredential, MaasVendor } from '@/proto/agentplatform/v2/maas_pb';
import { maasApi } from '@/api/maas.api';
import { useDeleteMaasCredential } from '../hooks/useMaasCredentials';
import { useTestMaasCredential } from '../hooks/useMaasModels';

interface Props {
  data: MaasCredential[];
  loading: boolean;
  onEdit: (row: MaasCredential) => void;
}

const VENDOR_LABEL: Record<MaasVendor, string> = {
  [MaasVendor.MAAS_VENDOR_UNSPECIFIED]: '未指定',
  [MaasVendor.InfiniAI]: 'InfiniAI',
};

export default function ModelCredentialTable({ data, loading, onEdit }: Props) {
  const { message, modal } = App.useApp();
  const deleteMut = useDeleteMaasCredential();
  const testMut = useTestMaasCredential();

  async function onCopy(row: MaasCredential) {
    try {
      const plain = await maasApi.copyCredentialKey(row.id);
      const ok = await copyToClipboard(plain);
      if (ok) message.success('已复制到剪贴板');
      else message.error('复制失败');
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  async function onTest(row: MaasCredential) {
    const close = message.loading(`测试 ${row.name} 中...`, 0);
    try {
      const res = await testMut.mutateAsync(row.id);
      close();
      message.success(`连通成功（${res.latencyMs} ms · ${res.modelCount} 个模型）`);
    } catch (e) {
      close();
      message.error(`连通失败：${e instanceof ApiError ? e.message : (e as Error).message}`);
    }
  }

  function onDelete(row: MaasCredential) {
    modal.confirm({
      title: '删除凭证',
      content: `确认删除凭证「${row.name}」？被 Agent 引用时无法删除。`,
      okType: 'danger',
      async onOk() {
        try {
          await deleteMut.mutateAsync(row.id);
          message.success('凭证已删除');
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : (e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<MaasCredential> = [
    {
      title: '凭证名称',
      key: 'name',
      minWidth: 100,
      render: (_, row) => (
        <div className="pp-cell-name">
          <span
            className="obj-icon-tinted"
            style={{ ['--c' as string]: 'var(--platform-c-credential)' }}
          >
            <DsIcon name="key-round" size={15} />
          </span>
          <div>
            <div className="name-text">{row.name}</div>
            <div className="id-text">{row.id}</div>
          </div>
        </div>
      ),
    },
    {
      title: '厂商 / Base URL',
      key: 'vendor',
      minWidth: 220,
      render: (_, r) => (
        <div>
          <div>{VENDOR_LABEL[r.vendor] ?? r.vendor}</div>
          <div className="creds-url">{r.baseUrl}</div>
        </div>
      ),
    },
    {
      title: '默认模型',
      key: 'defaultModel',
      minWidth: 160,
      render: (_, r) => (
        <Tag color="processing" bordered={false}>
          {r.defaultModel}
        </Tag>
      ),
    },
    {
      title: 'API Key',
      key: 'apiKey',
      render: (_, r) => <code className="pp-mono">{r.apiKey}</code>,
    },
    // {
    //   title: '最近调用',
    //   key: 'lastCalledAt',
    //   minWidth: 160,
    //   render: () => <span style={{ color: 'var(--text-color-placeholder)' }}>—</span>,
    // },
    {
      title: '创建者',
      key: 'userId',
      minWidth: 100,
      render: (_, r) => <UserName userId={r.userId} />,
    },
    {
      title: '创建时间',
      key: 'createdAt',
      minWidth: 160,
      render: (_, r) => formatTimestamp(r.createdAt),
    },
    {
      title: '更新时间',
      key: 'updatedAt',
      minWidth: 160,
      render: (_, r) => formatTimestamp(r.updatedAt),
    },
    {
      title: '操作',
      key: '__actions',
      width: 200,
      align: 'left',
      fixed: 'right',
      render: (_, r) => (
        <>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => onEdit(r)}
            />
          </Tooltip>
          <Tooltip title="测试连通性">
            <Button type="text" size="small" icon={<Zap size={14} />} onClick={() => onTest(r)} />
          </Tooltip>
          <Tooltip title="复制 API Key">
            <Button type="text" size="small" icon={<Copy size={14} />} onClick={() => onCopy(r)} />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              size="small"
              icon={<Trash2 size={14} />}
              onClick={() => onDelete(r)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <ProTable<MaasCredential>
      data={data}
      columns={columns}
      rowKey="id"
      size="middle"
      loading={loading}
      scroll={{ x: 'max-content' }}
      emptyChildren={
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-color-placeholder)' }}>
          还没有模型凭证 · 先去 cloud.infini-ai.com 申请 API Key
        </div>
      }
    />
  );
}
